/**
 * Microsoft Clarity integration utilities
 *
 * Clarity is loaded on-demand for specific use cases (e.g., walkthrough)
 * to minimize privacy impact while still gathering useful UX data.
 */

// Clarity function signature
type ClarityFunction = {
  (method: 'set', key: string, value: string): void;
  (method: 'identify', customId: string, customSessionId?: string): void;
  (method: 'consent'): void;
  (method: 'event', eventName: string): void;
  (method: 'stop'): void;
  q?: unknown[];
};

declare global {
  interface Window {
    clarity?: ClarityFunction;
  }
}

// Track initialization state
let clarityInitialized = false;
let clarityScriptElement: HTMLScriptElement | null = null;

/**
 * Check if Clarity is available and initialized
 */
const isClarityAvailable = (): boolean => {
  return (
    typeof window !== 'undefined' &&
    typeof window.clarity === 'function' &&
    clarityInitialized
  );
};

/**
 * Initialize Microsoft Clarity
 * Only loads in production builds when a project ID is configured
 *
 * @returns true if Clarity was initialized, false otherwise
 */
export const initClarity = (): boolean => {
  // Only run in production
  if (process.env.NODE_ENV !== 'production') {
    console.log('[Clarity] Skipped - not in production');
    return false;
  }

  const projectId = process.env.REACT_APP_CLARITY_PROJECT_ID;
  if (!projectId) {
    console.log('[Clarity] Skipped - no project ID configured');
    return false;
  }

  // Don't re-initialize if already running
  if (clarityInitialized) {
    console.log('[Clarity] Already initialized');
    return true;
  }

  try {
    // Initialize the clarity function queue
    type ClarityFunc = ((...args: unknown[]) => void) & { q?: unknown[] };
    type WindowWithClarity = Window & { clarity?: ClarityFunc };

    const w = window as WindowWithClarity;
    w.clarity =
      w.clarity ||
      (function () {
        const fn = function (...args: unknown[]) {
          (fn.q = fn.q || []).push(args);
        } as ClarityFunc;
        return fn;
      })();

    // Load the Clarity script
    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.clarity.ms/tag/${projectId}`;
    script.id = 'clarity-script';

    const firstScript = document.getElementsByTagName('script')[0];
    firstScript.parentNode?.insertBefore(script, firstScript);

    clarityScriptElement = script;
    clarityInitialized = true;

    console.log('[Clarity] Initialized for walkthrough tracking');
    return true;
  } catch (error) {
    console.warn('[Clarity] Failed to initialize:', error);
    return false;
  }
};

/**
 * Clear Clarity cookies
 * Removes _clck and _clsk cookies set by Clarity
 */
const clearClarityCookies = (): void => {
  const cookiesToClear = ['_clck', '_clsk'];
  const domains = [window.location.hostname, `.${window.location.hostname}`];

  cookiesToClear.forEach((cookieName) => {
    // Clear for current path and root path, on both exact domain and parent domain
    domains.forEach((domain) => {
      document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=${domain}`;
      document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/`;
    });
    // Also try without domain (for same-origin cookies)
    document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/`;
  });
};

/**
 * Stop Clarity tracking
 * Calls the stop method, then delays cleanup to allow queued events to be processed
 */
export const stopClarity = (): void => {
  if (!clarityInitialized) {
    return;
  }

  // Mark as not initialized immediately to prevent new events from being queued
  clarityInitialized = false;

  // Capture the current script element to remove (in case init is called again before cleanup)
  const scriptToRemove = clarityScriptElement;
  clarityScriptElement = null;

  try {
    // Call Clarity's stop method if available
    if (window.clarity) {
      window.clarity('stop');
      console.log('[Clarity] Stopped');
    }

    // Delay cleanup to allow queued events to be processed by the Clarity script
    // The script loads async and may still be processing queued events
    setTimeout(() => {
      try {
        // Remove the old script element (captured at stop time)
        if (scriptToRemove && scriptToRemove.parentNode) {
          scriptToRemove.parentNode.removeChild(scriptToRemove);
        }

        // Only clear window.clarity and cookies if Clarity hasn't been re-initialized
        // This prevents breaking a new session started during the delay
        if (!clarityInitialized) {
          delete window.clarity;
          clearClarityCookies();
          console.log('[Clarity] Cleanup complete');
        } else {
          console.log('[Clarity] Skipped cleanup - Clarity was re-initialized');
        }
      } catch (error) {
        console.warn('[Clarity] Failed to cleanup:', error);
      }
    }, 2000); // 2 second delay to allow event queue to flush
  } catch (error) {
    console.warn('[Clarity] Failed to stop:', error);
  }
};

/**
 * Identify the current user in Clarity
 * This links the Clarity session to your user ID for easier debugging
 *
 * @param userId - The Auth0 user ID (sub claim)
 * @param sessionId - Optional session ID for correlation
 */
export const clarityIdentify = (userId: string, sessionId?: string): void => {
  try {
    if (isClarityAvailable() && window.clarity) {
      window.clarity('identify', userId, sessionId);
    }
  } catch (error) {
    console.warn('[Clarity] Failed to identify user:', error);
  }
};

/**
 * Set a custom tag in Clarity for filtering sessions
 *
 * @param key - Tag name
 * @param value - Tag value
 */
export const claritySetTag = (key: string, value: string): void => {
  try {
    if (isClarityAvailable() && window.clarity) {
      window.clarity('set', key, value);
    }
  } catch (error) {
    console.warn('[Clarity] Failed to set tag:', error);
  }
};

/**
 * Track a custom event in Clarity
 *
 * @param eventName - Name of the event
 */
export const clarityEvent = (eventName: string): void => {
  try {
    if (isClarityAvailable() && window.clarity) {
      window.clarity('event', eventName);
    }
  } catch (error) {
    console.warn('[Clarity] Failed to track event:', error);
  }
};
